import type { ReactNode } from 'react';

import { formatArtifactSize } from './chatAttachments';
import {
  FILE_AVAILABILITY_DOWNLOAD_ONLY,
  FILE_AVAILABILITY_NONE,
  FILE_AVAILABILITY_PREVIEW_READY,
  FILE_AVAILABILITY_TEXT_READY,
  formatUploadedFilePreview
} from './chatArtifactUtils';
import {
  hasMoreFileText,
  resolveFileAvailabilityState,
  type FileTextSliceRecord,
  type UploadedFileRecord
} from './chatArtifactUtils';

interface ChatFilePreviewPaneProps {
  artifactId: string;
  file?: UploadedFileRecord | null;
  mimeType?: string | null;
  sizeBytes?: number;
  text: string;
  fileText?: FileTextSliceRecord;
  objectUrl?: string;
  loading?: boolean;
  onLoadMore?: () => void;
  onDownload?: () => void;
  renderPreview?: (artifactId: string, content: string, options?: { showLineNumbers?: boolean }) => ReactNode;
}

const availabilityLabel = (availabilityState: string): string | null => {
  switch (availabilityState) {
    case FILE_AVAILABILITY_TEXT_READY:
      return 'Text preview';
    case FILE_AVAILABILITY_PREVIEW_READY:
      return 'Inline preview';
    case FILE_AVAILABILITY_DOWNLOAD_ONLY:
      return 'Download only';
    case FILE_AVAILABILITY_NONE:
      return 'Blocked';
    default:
      return null;
  }
};

const renderFacts = (artifactId: string, facts: string[]) => {
  if (facts.length === 0) {
    return null;
  }
  return (
    <div className="chat-inspector__file-meta">
      {facts.map((fact) => (
        <span key={`${artifactId}-fact-${fact}`} className="chat-inspector__file-meta-pill">
          {fact}
        </span>
      ))}
    </div>
  );
};

const ChatFilePreviewPane = ({
  artifactId,
  file,
  mimeType,
  sizeBytes,
  text,
  fileText,
  objectUrl,
  loading = false,
  onLoadMore,
  onDownload,
  renderPreview
}: ChatFilePreviewPaneProps) => {
  const resolvedMimeType = file?.mimeType ?? fileText?.mimeType ?? mimeType ?? null;
  const resolvedSize = typeof file?.sizeBytes === 'number' ? file.sizeBytes : sizeBytes;
  const sizeLabel = formatArtifactSize(resolvedSize);
  const availabilityState = resolveFileAvailabilityState({
    availabilityState: file?.availabilityState,
    mimeType: resolvedMimeType
  });
  const availabilityPill = availabilityLabel(availabilityState);
  const analysisState = typeof file?.analysisState === 'string' ? file.analysisState.trim() : '';
  const analysisFamily = typeof file?.analysisFamily === 'string' ? file.analysisFamily.trim() : '';
  const preferredRep = typeof file?.preferredRep === 'string' ? file.preferredRep.trim() : '';
  const readyRepCount = Array.isArray(file?.reps) ? file.reps.filter((rep): rep is string => typeof rep === 'string' && rep.trim().length > 0).length : 0;
  const lineSummary =
    availabilityState === FILE_AVAILABILITY_TEXT_READY && fileText && fileText.totalLines > 0
      ? `Lines ${fileText.lines[0]?.lineNo ?? 1}-${fileText.endLine} of ${fileText.totalLines}`
      : null;
  const analysisPill = analysisState && analysisState !== 'none' ? `Analysis ${analysisState}` : null;
  const preferredRepPill = preferredRep ? `Prefers ${preferredRep}` : null;
  const readyRepPill = readyRepCount > 0 ? `${readyRepCount} ready rep${readyRepCount === 1 ? '' : 's'}` : null;
  const facts = [
    resolvedMimeType,
    sizeLabel,
    availabilityPill,
    analysisFamily || null,
    analysisPill,
    preferredRepPill,
    readyRepPill,
    lineSummary
  ].filter(Boolean) as string[];

  if (availabilityState === FILE_AVAILABILITY_PREVIEW_READY) {
    const isImage = (resolvedMimeType ?? '').toLowerCase().startsWith('image/');
    return (
      <div className="chat-inspector__preview">
        {renderFacts(artifactId, facts)}
        <div className="chat-inspector__pre-wrap">
          {objectUrl ? (
            isImage ? (
              <img src={objectUrl} alt={file?.filename ?? 'File preview'} style={{ display: 'block', maxWidth: '100%' }} />
            ) : (
              <iframe title={file?.filename ?? 'File preview'} src={objectUrl} style={{ width: '100%', minHeight: '32rem', border: 0 }} />
            )
          ) : (
            <div className="chat-inspector__empty">
              <p>{loading ? 'Loading preview…' : 'Preview unavailable.'}</p>
            </div>
          )}
        </div>
        {onDownload && (
          <div className="chat-inspector__cta">
            <span>Download the original file bytes.</span>
            <button type="button" onClick={onDownload} disabled={loading}>
              Download file
            </button>
          </div>
        )}
      </div>
    );
  }

  if (availabilityState === FILE_AVAILABILITY_DOWNLOAD_ONLY) {
    return (
      <div className="chat-inspector__preview">
        {renderFacts(artifactId, facts)}
        <div className="chat-inspector__empty">
          <p>This file is available for download, but this phase does not provide an inline semantic preview for it.</p>
          {file && <pre className="chat-inspector__pre chat-inspector__pre--terminal chat-inspector__pre--wrap">{formatUploadedFilePreview(file)}</pre>}
        </div>
        {onDownload && (
          <div className="chat-inspector__cta">
            <span>Download the original file for external inspection.</span>
            <button type="button" onClick={onDownload} disabled={loading}>
              Download file
            </button>
          </div>
        )}
      </div>
    );
  }

  if (availabilityState === FILE_AVAILABILITY_NONE) {
    return (
      <div className="chat-inspector__preview">
        {renderFacts(artifactId, facts)}
        <div className="chat-inspector__empty">
          <p>{file?.blockedReason?.trim() || 'This file is blocked and cannot be previewed in the chat surface.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-inspector__preview">
      {renderFacts(artifactId, facts)}
      <div className="chat-inspector__pre-wrap">
        <pre className="chat-inspector__pre chat-inspector__pre--terminal chat-inspector__pre--wrap">
          {renderPreview ? renderPreview(artifactId, text, { showLineNumbers: true }) : text}
        </pre>
      </div>
      <div className="chat-inspector__cta">
        {fileText && hasMoreFileText(fileText) && onLoadMore ? (
          <>
            <span>More file content is available.</span>
            <button type="button" onClick={onLoadMore} disabled={loading}>
              Load more lines
            </button>
          </>
        ) : (
          <span>Text preview is ready.</span>
        )}
        {onDownload && (
          <button type="button" onClick={onDownload} disabled={loading}>
            Download file
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatFilePreviewPane;
